import { DateTime } from "luxon";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater, useImmer } from "use-immer";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { ExperienceFe } from "@intelligems/ig-types";
import {
  ensureNumber,
  getEndedAtTs,
  getFirstStartedAtTs,
  getPausedAtTs,
} from "@intelligems/ig-utils";
import { Grid, Popover, useTheme } from "@mui/material";
import { getTinybirdActiveAnalyticsFilters } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { DateRangePicker } from "@src/features/analytics/analyticsFilters/DateRangePicker";
import {
  getInitialStartFilterTime,
  getLastEndFilterTime,
} from "@src/features/analytics/analyticsFilters/utils";
import {
  getDefaultHiddenEndTime,
  getDefaultPersonalizationStartTime,
} from "@src/store/helpers";
import {
  selectedDashboardFilters,
  setDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { mpClickAnalyticsFilterSaveButton } from "@src/utils/mixpanel";

interface AnalyticsDateRangeModalProps {
  anchorEl: HTMLDivElement | null;
  setAnchorEl: Updater<HTMLDivElement | null>;
  experience: ExperienceFe;
}
export const AnalyticsDateRangeModal = ({
  anchorEl,
  setAnchorEl,
  experience,
}: AnalyticsDateRangeModalProps) => {
  const theme = useTheme();
  const dashboardFilters = useSelector(selectedDashboardFilters);
  const maxZScoreConfig = useConfiguration("max_z_score");
  const organization = useGetOrg();
  const [startTime, setStartTime] = useImmer<number | null>(
    getInitialStartFilterTime(experience, dashboardFilters)
  );
  const [endTime, setEndTime] = useImmer<number | null>(
    getLastEndFilterTime(experience, dashboardFilters)
  );

  const handleClose = () => {
    setStartTime(getInitialStartFilterTime(experience, dashboardFilters));
    setEndTime(null);
    setAnchorEl(null);
  };
  const [isStartValid, setIsStartValid] = useImmer<boolean>(true);
  const [isEndValid, setIsEndValid] = useImmer<boolean>(true);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const onOkClick = () => {
    dispatch(setDashboardFilter({ ...dashboardFilters, endTime, startTime }));
    const getTbActiveFilters = getTinybirdActiveAnalyticsFilters(
      {
        ...dashboardFilters,
        endTime,
        startTime,
      },
      maxZScoreConfig!.value
    );
    if (Object.keys(getTbActiveFilters).length > 0) {
      mpClickAnalyticsFilterSaveButton(
        experience.id!,
        getTbActiveFilters,
        dashboardFilters.pageName || "overview"
      );
    }
    handleClose();
  };

  const onResetClick = () => {
    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        endTime:
          experience.category === "experiment"
            ? null
            : getDefaultHiddenEndTime(experience),
        startTime:
          experience.category === "experiment"
            ? null
            : getDefaultPersonalizationStartTime(
                experience,
                organization?.data?.org?.timezone || undefined
              ),
      })
    );
    handleClose();
  };

  const onTimeChange = (
    newValue: DateTime | null,
    filterType: "start" | "end"
  ) => {
    if (filterType === "start") {
      setIsStartValid(newValue === null ? false : newValue.isValid);
      setStartTime((draft) => {
        draft = newValue?.toMillis() ?? null;
        return draft;
      });
    } else {
      setIsEndValid(newValue === null ? true : newValue.isValid);
      setEndTime((draft) => {
        draft = newValue?.toMillis() ?? null;
        return draft;
      });
    }
  };

  return (
    <Popover
      onClose={handleClose}
      anchorEl={anchorEl}
      open={open}
      sx={{ borderRadius: "5px" }}
      id={"date-range"}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "5px",
            boxShadow: theme.igShadows.low,
          },
          elevation: 0,
        },
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <IgPaper sx={{ padding: "16px 24px" }}>
        <Grid container direction={"column"} rowSpacing={4}>
          <Grid item>
            <IgTypography
              sx={{
                fontWeight: 600,
              }}
            >
              Date Range
            </IgTypography>
          </Grid>
          <Grid item>
            <DateRangePicker
              startFilter={DateTime.fromMillis(ensureNumber(startTime) ?? 0)}
              endFilter={
                endTime ? DateTime.fromMillis(ensureNumber(endTime)) : null
              }
              onTimeChange={onTimeChange}
              resourceMinTime={
                getFirstStartedAtTs(experience.experienceActions) ?? 0
              }
              isEndValid={isEndValid}
              isStartValid={isStartValid}
              resourceMaxTime={
                experience.status === "ended"
                  ? (getEndedAtTs(experience.experienceActions)?.actionTs ?? 0)
                  : experience.status === "paused"
                    ? (getPausedAtTs(experience.experienceActions)?.actionTs ??
                      0)
                    : undefined
              }
            />
          </Grid>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"space-between"}
          >
            <Grid item>
              <IgButton
                size={"small"}
                color={"secondary"}
                onClick={onResetClick}
                sx={{ minHeight: "30px", maxHeight: "30px" }}
              >
                {experience.category === "personalization" ? "Reset" : "Clear"}
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                sx={{ minHeight: "30px", maxHeight: "30px" }}
                variant={"contained"}
                size={"small"}
                onClick={onOkClick}
              >
                Ok
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      </IgPaper>
    </Popover>
  );
};
